import type { SomeJiraIssueField } from '../types/fields-type.tsx';
import type {
	FieldTransformer,
	ConnectionTransformer,
	Connection,
} from '../types/transformer-type.tsx';

type BaseFieldTransformer = (
	...args: Parameters<FieldTransformer>
) => ReturnType<FieldTransformer> & {
	key: string;
	value: null;
	schema: {
		type: string;
		custom: null;
		system: null;
	};
};

const isEditableFromFieldConfig = (node: Pick<SomeJiraIssueField, 'fieldConfig'>) => {
	return Boolean(
		'fieldConfig' in node &&
			node.fieldConfig != null &&
			'isEditable' in node.fieldConfig &&
			node.fieldConfig.isEditable,
	);
};

const isRequiredFromFieldConfig = (node: Pick<SomeJiraIssueField, 'fieldConfig'>) => {
	return Boolean(
		'fieldConfig' in node &&
			node.fieldConfig != null &&
			'isRequired' in node.fieldConfig &&
			node.fieldConfig.isRequired,
	);
};

/**
 * Provides default legacy field values for an AGG field.
 * Overriding `value`, `schema`, and other fields are left to the transformer.
 */
export const transformAggNodeToLegacyField: BaseFieldTransformer = (node) => ({
	ari: 'id' in node ? node.id : undefined,
	key: node.fieldId || '',
	title: node.name || '',
	editable: Boolean(
		'isEditableInIssueView' in node ? node.isEditableInIssueView : isEditableFromFieldConfig(node),
	),
	required: isRequiredFromFieldConfig(node),
	autoCompleteUrl: 'searchUrl' in node ? node.searchUrl : null,
	allowedValues: [],
	value: null,
	schema: {
		type: node.type || '',
		custom: null,
		system: null,
	},
	description: 'description' in node && node.description ? node.description : undefined,
});

export const transformAggConnection: ConnectionTransformer = <Node, TransformedNode>(
	connection?: Connection<Node> | null | undefined,
	transformCallback?: (node: Node) => TransformedNode,
) => {
	const edges = connection?.edges ?? [];

	if (transformCallback) {
		return edges
			.map((edge) => (edge?.node != null ? transformCallback(edge.node) : null))
			.filter(Boolean);
	}

	return edges.map((edge) => edge?.node).filter(Boolean);
};

// https://jdog.jira-dev.com/browse/BENTO-12624 - Connect fields return id as numbers
export const toLegacyConnectOption = (node?: { value: unknown; optionId: string } | null) => {
	if (!node) return null;

	return {
		value: node.value ?? null,
		id: parseInt(node.optionId, 10),
	};
};
